import { RoutesPaths } from "../../../constants/routes.ts"
import { type ProductTour } from "../tour/product-tour.types.ts"

/**
 * The core EZBuild operating story: Home → Lead → Project. Every FEATURE
 * step navigates to the real route, waits for the real content, and
 * spotlights it — never a slideshow parked on one screen. See
 * docs/kb for why this replaced the old single-route platform tour.
 *
 * The tour must be most useful on an empty account, so every step whose
 * `resolveRoute` needs a real Lead/Project falls back to the static,
 * hardcoded demo screens (`GuideTourPreviewLeadPage`/`GuideTourPreviewProjectPage`,
 * mounted at `RoutesPaths.guide.tourPreview.*`) instead of returning `null`
 * once `resolveFirstAccessibleLead`/`Project` has confirmed there isn't a
 * real one — never an honest-but-useless empty fallback, and never a real
 * record written anywhere. `open-lead`/`open-project` themselves are the one
 * exception: they stay on the real (possibly genuinely empty) Leads/Projects
 * list, which is the correct, honest thing to show there.
 */
export const PRODUCT_TOUR: ProductTour = {
  id: "product-tour",
  titleKey: "guide.productTour.intro.title",
  steps: [
    {
      id: "intro",
      type: "intro",
      titleKey: "guide.productTour.intro.title",
      bodyKey: "guide.productTour.intro.body",
      ctaKey: "guide.productTour.cta.start",
      resolveRoute: () => RoutesPaths.home
    },
    {
      id: "home-dashboard",
      type: "feature",
      titleKey: "guide.productTour.steps.homeDashboard.title",
      bodyKey: "guide.productTour.steps.homeDashboard.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: () => RoutesPaths.home,
      target: "home-dashboard-panel",
      placement: "right"
    },
    {
      id: "home-financial",
      type: "feature",
      titleKey: "guide.productTour.steps.homeFinancial.title",
      bodyKey: "guide.productTour.steps.homeFinancial.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: () => RoutesPaths.home,
      target: "home-financial-snapshot-panel",
      placement: "right"
    },
    {
      id: "dashboard-my-view",
      type: "feature",
      contextKey: "guide.productTour.context.dashboard",
      titleKey: "guide.productTour.steps.dashboardMyView.title",
      bodyKey: "guide.productTour.steps.dashboardMyView.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: () => RoutesPaths.home,
      target: "dashboard-myview-button",
      placement: "bottom"
    },
    {
      id: "dashboard-edit-layout",
      type: "feature",
      contextKey: "guide.productTour.context.dashboard",
      titleKey: "guide.productTour.steps.dashboardEditLayout.title",
      bodyKey: "guide.productTour.steps.dashboardEditLayout.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: () => RoutesPaths.home,
      target: "dashboard-edit-layout-button",
      placement: "bottom"
    },
    {
      // The one step that actually flips Edit Layout on — via the same
      // `?tourEdit=1` query param the real "Edit Layout" button's own
      // `startEdit()` call reacts to (WidgetsDashboard.tsx), never a
      // second/fake edit-mode implementation. Nothing here calls `save()`,
      // so it never persists.
      id: "dashboard-add-tile",
      type: "feature",
      contextKey: "guide.productTour.context.dashboard",
      titleKey: "guide.productTour.steps.dashboardAddTile.title",
      bodyKey: "guide.productTour.steps.dashboardAddTile.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: () => `${RoutesPaths.home}?tourEdit=1`,
      target: "dashboard-add-tile-button",
      placement: "bottom"
    },
    {
      id: "dashboard-widget-selection",
      type: "feature",
      contextKey: "guide.productTour.context.dashboard",
      titleKey: "guide.productTour.steps.dashboardWidgetSelection.title",
      bodyKey: "guide.productTour.steps.dashboardWidgetSelection.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: () => `${RoutesPaths.home}?tourEdit=1&tourAddTile=1`,
      target: "dashboard-add-tile-list",
      placement: "left"
    },
    {
      id: "dashboard-rearrange",
      type: "feature",
      contextKey: "guide.productTour.context.dashboard",
      titleKey: "guide.productTour.steps.dashboardRearrange.title",
      bodyKey: "guide.productTour.steps.dashboardRearrange.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: () => `${RoutesPaths.home}?tourEdit=1`,
      target: "dashboard-rearrange-panel",
      placement: "right"
    },
    {
      id: "nav-leads",
      type: "navigation",
      titleKey: "guide.productTour.steps.navLeads.title",
      bodyKey: "guide.productTour.steps.navLeads.body",
      ctaKey: "guide.productTour.cta.goToLeads",
      resolveRoute: () => RoutesPaths.home,
      target: "nav-leads",
      requiredPage: "leads",
      placement: "right"
    },
    {
      id: "leads-workspace",
      type: "feature",
      titleKey: "guide.productTour.steps.leadsWorkspace.title",
      bodyKey: "guide.productTour.steps.leadsWorkspace.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: () => RoutesPaths.lead.list,
      target: "leads-pipeline-table",
      requiredPage: "leads",
      placement: "top"
    },
    {
      // Stays on the real Leads list — the record is resolved here (so it's
      // ready by the time the next step needs it) but this step's own
      // target lives on THIS page, not the Lead's. Opening the Lead itself
      // is what the next step's own navigation does.
      id: "open-lead",
      type: "action",
      contextKey: "guide.productTour.context.lead",
      titleKey: "guide.productTour.steps.openLead.title",
      bodyKey: "guide.productTour.steps.openLead.body",
      ctaKey: "guide.productTour.cta.openLead",
      resolveRoute: () => RoutesPaths.lead.list,
      target: "leads-pipeline-table",
      requiredPage: "leads",
      requiresRecord: "lead",
      placement: "top"
    },
    {
      id: "lead-overview",
      type: "feature",
      contextKey: "guide.productTour.context.leadOverview",
      titleKey: "guide.productTour.steps.leadOverview.title",
      bodyKey: "guide.productTour.steps.leadOverview.body",
      ctaKey: "guide.productTour.cta.seeScope",
      resolveRoute: (ctx) =>
        ctx.leadId ? RoutesPaths.lead.view(ctx.leadId) : RoutesPaths.guide.tourPreview.lead,
      target: "lead-overview-panel",
      requiredPage: "leads",
      requiresRecord: "lead",
      placement: "right"
    },
    {
      id: "lead-scope",
      type: "feature",
      contextKey: "guide.productTour.context.leadScope",
      titleKey: "guide.productTour.steps.leadScope.title",
      bodyKey: "guide.productTour.steps.leadScope.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: (ctx) =>
        ctx.leadId
          ? RoutesPaths.lead.view(ctx.leadId, { tab: "scope" })
          : `${RoutesPaths.guide.tourPreview.lead}?tab=scope`,
      target: "lead-scope-panel",
      requiredPage: "leads",
      requiresRecord: "lead",
      placement: "right"
    },
    {
      id: "lead-sales-flow",
      type: "feature",
      contextKey: "guide.productTour.context.leadDocuments",
      titleKey: "guide.productTour.steps.leadSalesFlow.title",
      bodyKey: "guide.productTour.steps.leadSalesFlow.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: (ctx) =>
        ctx.leadId
          ? RoutesPaths.lead.view(ctx.leadId, { tab: "documents" })
          : `${RoutesPaths.guide.tourPreview.lead}?tab=documents`,
      target: "lead-documents-panel",
      requiredPage: "leads",
      requiresRecord: "lead",
      // A REAL Lead may not be in a stage that shows Proposals & Contracts
      // yet (the real app itself falls back to Overview when it isn't) —
      // teach the concept anyway rather than showing nothing. The demo Lead
      // always has both, so this only ever fires for a real, mid-pipeline
      // Lead — never for the empty-account case, which now has its own real
      // Documents tab on the demo page instead.
      fallbackTitleKey: "guide.productTour.steps.leadSalesFlow.title",
      fallbackBodyKey: "guide.productTour.steps.leadSalesFlow.body",
      fallbackTarget: "lead-overview-panel",
      placement: "right"
    },
    {
      id: "lead-to-project",
      type: "transition",
      titleKey: "guide.productTour.steps.leadToProject.title",
      bodyKey: "guide.productTour.steps.leadToProject.body",
      ctaKey: "guide.productTour.cta.seeProjects",
      resolveRoute: (ctx) =>
        ctx.leadId ? RoutesPaths.lead.view(ctx.leadId) : RoutesPaths.guide.tourPreview.lead,
      requiredPage: "leads"
    },
    {
      id: "projects-workspace",
      type: "feature",
      titleKey: "guide.productTour.steps.projectsWorkspace.title",
      bodyKey: "guide.productTour.steps.projectsWorkspace.body",
      ctaKey: "guide.productTour.cta.continue",
      resolveRoute: () => RoutesPaths.project.list,
      target: "projects-list-panel",
      requiredPage: "projects",
      placement: "top"
    },
    {
      // Same reasoning as open-lead: stays on the real Projects list. The
      // record is resolved here so it's ready for the next step, but this
      // step's own target lives on THIS page.
      id: "open-project",
      type: "action",
      contextKey: "guide.productTour.context.project",
      titleKey: "guide.productTour.steps.openProject.title",
      bodyKey: "guide.productTour.steps.openProject.body",
      ctaKey: "guide.productTour.cta.openProject",
      resolveRoute: () => RoutesPaths.project.list,
      target: "projects-list-panel",
      requiredPage: "projects",
      requiresRecord: "project",
      placement: "top"
    },
    {
      id: "project-overview",
      type: "feature",
      contextKey: "guide.productTour.context.projectOverview",
      titleKey: "guide.productTour.steps.projectOverview.title",
      bodyKey: "guide.productTour.steps.projectOverview.body",
      ctaKey: "guide.productTour.cta.seeMilestones",
      resolveRoute: (ctx) =>
        ctx.projectId
          ? RoutesPaths.project.view(ctx.projectId).overview
          : RoutesPaths.guide.tourPreview.project,
      target: "project-header-panel",
      requiredPage: "projects",
      requiresRecord: "project",
      placement: "bottom"
    },
    {
      id: "project-milestones",
      type: "feature",
      contextKey: "guide.productTour.context.projectMilestones",
      titleKey: "guide.productTour.steps.projectMilestones.title",
      bodyKey: "guide.productTour.steps.projectMilestones.body",
      ctaKey: "guide.productTour.cta.seeFinance",
      resolveRoute: (ctx) =>
        ctx.projectId
          ? RoutesPaths.project.view(ctx.projectId).milestones
          : RoutesPaths.guide.tourPreview.projectMilestones,
      target: "project-milestones-panel",
      requiredPage: "projects.milestones.project_milestone_view",
      requiresRecord: "project",
      placement: "left"
    },
    {
      id: "project-finance",
      type: "feature",
      contextKey: "guide.productTour.context.projectFinance",
      titleKey: "guide.productTour.steps.projectFinance.title",
      bodyKey: "guide.productTour.steps.projectFinance.body",
      ctaKey: "guide.productTour.cta.seeInvoices",
      resolveRoute: (ctx) =>
        ctx.projectId
          ? RoutesPaths.project.view(ctx.projectId).finance
          : RoutesPaths.guide.tourPreview.projectFinance,
      target: "project-finance-panel",
      requiredPage: "projects.finances.project_finance_view",
      requiresRecord: "project",
      placement: "left"
    },
    {
      id: "project-invoices",
      type: "feature",
      contextKey: "guide.productTour.context.projectInvoices",
      titleKey: "guide.productTour.steps.projectInvoices.title",
      bodyKey: "guide.productTour.steps.projectInvoices.body",
      ctaKey: "guide.productTour.cta.seeFiles",
      resolveRoute: (ctx) =>
        ctx.projectId
          ? RoutesPaths.project.view(ctx.projectId).invoices
          : RoutesPaths.guide.tourPreview.projectInvoices,
      target: "project-invoices-panel",
      requiredPage: "projects.invoices.project_invoice_view",
      requiresRecord: "project",
      placement: "left"
    },
    {
      id: "project-files",
      type: "feature",
      contextKey: "guide.productTour.context.projectFiles",
      titleKey: "guide.productTour.steps.projectFiles.title",
      bodyKey: "guide.productTour.steps.projectFiles.body",
      ctaKey: "guide.productTour.cta.seeActivity",
      resolveRoute: (ctx) =>
        ctx.projectId
          ? RoutesPaths.project.view(ctx.projectId).files()
          : RoutesPaths.guide.tourPreview.projectFiles,
      target: "project-files-panel",
      requiredPage: "projects.docs.project_doc_view",
      requiresRecord: "project",
      placement: "left"
    },
    {
      id: "project-activity",
      type: "feature",
      contextKey: "guide.productTour.context.projectActivity",
      titleKey: "guide.productTour.steps.projectActivity.title",
      bodyKey: "guide.productTour.steps.projectActivity.body",
      ctaKey: "guide.productTour.cta.continueTour",
      resolveRoute: (ctx) =>
        ctx.projectId
          ? RoutesPaths.project.view(ctx.projectId).logs
          : RoutesPaths.guide.tourPreview.projectActivity,
      target: "project-activity-panel",
      requiredPage: "projects.features.project_activity_log",
      requiresRecord: "project",
      placement: "left"
    },
    {
      id: "complete",
      type: "complete",
      titleKey: "guide.productTour.complete.title",
      bodyKey: "guide.productTour.complete.body",
      ctaKey: "guide.productTour.complete.goToDashboard"
    }
  ]
}
